import {
  BpkButtonV2,
  BUTTON_TYPES,
  SIZE_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import { withRtlSupport } from '@skyscanner/backpack-web/bpk-component-icon';
import ArrowIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/long-arrow-right';
import BpkImage, {
  withLazyLoading,
} from '@skyscanner/backpack-web/bpk-component-image';
import BpkLink from '@skyscanner/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';

import { getLocalFormattedDateFromISO } from '../localisation-helpers';

import type { ArticleCardProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ArticleCardProps';

import STYLES from './ArticleCard.module.scss';

const isBrowser = typeof window !== 'undefined';
// Support for SSR
const documentIfExists = isBrowser ? document : null;
// withLazyLoading will not use document when rendered server side so it's safe
// pass null.
const LazyLoadedImage = withLazyLoading(BpkImage, documentIfExists);

const RtlArrowIcon = withRtlSupport(ArrowIcon);

const ArticleCard = ({
  articleCreatedDate,
  articleHref,
  aspectRatio,
  author,
  authorHref,
  body,
  cta,
  fallbackArticleCreatedLocalised,
  image,
  title,
  variant,
}: ArticleCardProps) => {
  const articleCreatedLocalised =
    isBrowser && articleCreatedDate && variant !== 'editorialCrossLink'
      ? getLocalFormattedDateFromISO({
          isoDate: articleCreatedDate,
        })
      : fallbackArticleCreatedLocalised;

  return (
    <div
      data-testid="article-card"
      className={[STYLES.ArticleCard, STYLES[`ArticleCard__${variant}`]].join(
        ' ',
      )}
    >
      <a
        href={articleHref}
        target="_blank"
        className={STYLES.ArticleLink}
        aria-label={title}
        rel="noreferrer"
      />
      <div className={[STYLES.Image, STYLES[`Image__${variant}`]].join(' ')}>
        <LazyLoadedImage src={image.src} altText="" aspectRatio={aspectRatio} />
      </div>

      <div className={STYLES[`ArticleTitle__${variant}`]}>
        <BpkText tagName="h3">{title}</BpkText>
      </div>

      <div className={STYLES.DateAuthor}>
        <BpkText tagName="p" textStyle={TEXT_STYLES.footnote}>
          <time dateTime={articleCreatedDate}>{articleCreatedLocalised}</time>
        </BpkText>

        <BpkLink
          implicit={variant === 'editorialCrossLink'}
          rel="author"
          href={authorHref}
          target="_blank"
          className={[STYLES.AuthorLink, STYLES[`AuthorLink__${variant}`]].join(
            ' ',
          )}
        >
          {author}
        </BpkLink>
      </div>

      {variant === 'cardSet' && (
        <>
          {body && (
            <div className={STYLES.Body}>
              <BpkText tagName="p" textStyle={TEXT_STYLES.bodyLongform}>
                {body}
              </BpkText>
            </div>
          )}
          {cta && (
            <div className={STYLES.Cta}>
              <BpkButtonV2
                type={BUTTON_TYPES.link}
                size={SIZE_TYPES.large}
                href={articleHref}
                implicit
              >
                {cta.label} <RtlArrowIcon />
              </BpkButtonV2>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ArticleCard;
